import { useCallback, useMemo } from 'react';
import { useAsync } from 'react-use';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { useApi, identityApiRef } from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  entityPresentationApiRef,
  entityPresentationSnapshot,
} from '@backstage/plugin-catalog-react';
import {
  sortRefs,
  toCanonicalOwnerRef,
} from '../components/insights/insights-group-scope';
import {
  PULSE_CACHE_KEYS,
  PULSE_QUERY_CACHE_POLICIES,
  useCachedQuery,
} from './cache';

export type GroupOption = { value: string; label: string };

const GROUP_ENTITY_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'spec.type',
  'spec.profile.displayName',
  'relations',
];

/**
 * Resolves the current user's teams from the catalog, mirroring the sidebar's
 * "My Teams": every group the user owns whose `spec.type` is `team`.
 *
 * Shared by the check and track detail pages so the "Only my teams" switch
 * behaves consistently.
 */
export const useMyTeams = () => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);
  const { value: identity, loading: identityLoading } = useAsync(
    () => identityApi.getBackstageIdentity(),
    [identityApi],
  );

  const fetchGroups = useCallback(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'Group' },
      fields: GROUP_ENTITY_FIELDS,
    });
    return response.items;
  }, [catalogApi]);
  const { value: allGroups = [], loading: groupsLoading } = useCachedQuery({
    cacheKey: PULSE_CACHE_KEYS.catalogGroups(identity?.userEntityRef ?? ''),
    fetcher: fetchGroups,
    ...PULSE_QUERY_CACHE_POLICIES.definitions,
    enabled: Boolean(identity),
  });

  const ownerByRef = useMemo(() => {
    const map = new Map<string, Entity>();
    for (const group of allGroups) {
      map.set(toCanonicalOwnerRef(stringifyEntityRef(group)), group);
    }
    return map;
  }, [allGroups]);

  // The org providers only set `spec.profile.displayName` on groups, which the
  // Backstage presentation API prefers over `metadata.title`/`metadata.name`.
  const labelByRef = useMemo(() => {
    const map = new Map<string, string>();
    for (const [ref, entity] of ownerByRef) {
      map.set(
        ref,
        entityPresentationSnapshot(
          entity,
          { defaultKind: 'Group' },
          entityPresentationApi,
        ).primaryTitle,
      );
    }
    return map;
  }, [ownerByRef, entityPresentationApi]);

  const groupOptions = useMemo<GroupOption[]>(
    () =>
      [...labelByRef.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [labelByRef],
  );

  // Non-group ownership refs (such as the user itself) never resolve against
  // `ownerByRef`, which only holds `Group` entities, so they drop out here.
  const ownedGroupRefs = useMemo(() => {
    const refs = (identity?.ownershipEntityRefs ?? [])
      .map(toCanonicalOwnerRef)
      .filter(ref => ownerByRef.has(ref));
    return sortRefs([...new Set(refs)]);
  }, [identity, ownerByRef]);

  const teamRefs = useMemo(
    () =>
      ownedGroupRefs.filter(ref => ownerByRef.get(ref)?.spec?.type === 'team'),
    [ownedGroupRefs, ownerByRef],
  );

  // Prefer an actual team over the areas/units the user also owns.
  const primaryOwnedGroupRef = teamRefs[0] ?? ownedGroupRefs[0];

  return {
    ownerByRef,
    labelByRef,
    groupOptions,
    teamRefs,
    ownedGroupRefs,
    primaryOwnedGroupRef,
    loading: identityLoading || groupsLoading,
  };
};
